<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Khata</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="dataLoading" class="inline-block w-28 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
            <span v-else>{{ filtered.length }} of {{ customerBalances.length }} customer{{ customerBalances.length !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button @click="openAddCustomer"
          class="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          <span class="hidden sm:inline">Add Customer</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- ── MOBILE HEADER + STATS + SEARCH ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Indigo gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Khata</h1>
              <p class="text-indigo-200 text-xs mt-0.5">
                <span v-if="dataLoading">Loading...</span>
                <span v-else>{{ filtered.length}} of {{ customerBalances.length }} customer{{ customerBalances.length !== 1 ? 's' : '' }}</span>
              </p>
            </div>
          </div>
          <!-- Stats row -->
          <div v-if="!dataLoading" class="grid grid-cols-2 gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-red-300 uppercase tracking-widest mb-1">Maine Lene Hain</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(summary.total_to_receive) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Maine Dene Hain</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(summary.total_to_give) }}</p>
            </div>
          </div>
          <!-- Stats skeleton -->
          <div v-else class="grid grid-cols-2 gap-2 mb-2">
            <div v-for="i in 2" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Search card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-1">
          <!-- Search input -->
          <div class="relative">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="search" placeholder="Search customers..."
              autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Search + Stats (desktop only) -->
      <div class="hidden md:flex items-center gap-3 mb-3">
        <!-- Search -->
        <div class="relative w-56">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="search" placeholder="Search..."
            autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-9 pr-8 py-1.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition" />
          <button v-if="search" @click="search = ''"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-500 dark:text-slate-300 transition">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Spacer -->
        <div class="flex-1"></div>

        <!-- Stats -->
        <div class="hidden md:flex items-center gap-2">
          <template v-if="dataLoading">
            <div class="h-10 w-36 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-36 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
            <p class="text-[10px] font-semibold text-red-400 uppercase tracking-wide">Maine Lene Hain</p>
            <p class="text-sm font-bold text-red-500 dark:text-red-400">Rs {{ formatNum(summary.total_to_receive) }}</p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
            <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">Maine Dene Hain</p>
            <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(summary.total_to_give) }}</p>
          </div>
          </template>
        </div>
      </div>

      <!-- Skeleton -->
      <template v-if="dataLoading">
        <!-- Desktop skeleton -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-8">
            <div v-for="i in 4" :key="i" class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 8" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-3 w-8 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse shrink-0"></div>
            <div class="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
        </div>
        <!-- Mobile skeleton -->
        <div class="md:hidden space-y-2.5">
          <div v-for="i in 6" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div class="flex items-center gap-3 px-4 py-3.5">
              <div class="w-11 h-11 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              </div>
              <div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse shrink-0"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state (desktop only) -->
      <div v-else-if="filtered.length === 0" class="hidden md:flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ search ? 'No customers match your search' : 'No customers yet' }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ search ? 'Try a different keyword' : 'Add your first customer to get started' }}
        </p>
        <button v-if="search" @click="search = ''" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear search</button>
      </div>

      <!-- Desktop table -->
      <div v-else class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
        <table class="w-full text-sm border-collapse" style="border-radius: inherit;">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-800">
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-12">#</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-red-400 uppercase tracking-wide w-40">Maine Lene Hain</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-emerald-500 uppercase tracking-wide w-40">Maine Dene Hain</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(c, i) in filtered" :key="c.id"
              @click="navigateToDetails(c.id)"
              class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer">
              <td class="px-5 py-4 text-slate-400 text-xs">{{ i + 1 }}</td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-sm font-bold shrink-0">
                    {{ c.name.charAt(0).toUpperCase() }}
                  </div>
                  <div>
                    <p class="font-semibold text-slate-800 dark:text-white">{{ c.name }}</p>
                    <p class="text-xs text-slate-400 mt-0.5">{{ c.shop_name }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4 text-right">
                <span v-if="c.balance > 0" class="font-semibold text-red-500 dark:text-red-400">
                  Rs {{ formatNum(c.balance) }}
                </span>
                <span v-else class="text-slate-400 dark:text-slate-600">Rs 0</span>
              </td>
              <td class="px-5 py-4 text-right">
                <span v-if="c.balance < 0" class="font-semibold text-emerald-600 dark:text-emerald-400">
                  Rs {{ formatNum(Math.abs(c.balance)) }}
                </span>
                <span v-else class="text-slate-400 dark:text-slate-600">Rs 0</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards (native style) -->
      <div v-if="!dataLoading" class="md:hidden space-y-2.5">
        <!-- Empty state -->
        <div v-if="filtered.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
          <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
            </svg>
          </div>
          <p class="text-slate-600 dark:text-slate-400 font-medium">
            {{ search ? 'No customers match' : 'No customers yet' }}
          </p>
          <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
            {{ search ? 'Try a different keyword' : 'Add your first customer to get started' }}
          </p>
          <button v-if="search" @click="search = ''" class="mt-4 text-indigo-500 text-sm font-semibold">Clear search</button>
        </div>

        <div v-for="c in filtered" :key="c.id"
          @click="navigateToDetails(c.id)"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden active:scale-98 transition cursor-pointer">

          <!-- Card face -->
          <div class="flex items-center gap-3 px-4 py-3.5">
            <!-- Colored avatar -->
            <div class="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-bold shrink-0"
              :style="{ backgroundColor: avatarColor(c.name) }">
              {{ c.name.charAt(0).toUpperCase() }}
            </div>
            <!-- Name + shop -->
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-slate-800 dark:text-white text-sm truncate">{{ c.name }}</p>
              <p class="text-xs text-slate-400 mt-0.5 truncate">{{ c.shop_name }}</p>
            </div>
            <!-- Balance badge -->
            <div class="shrink-0 text-right">
              <p v-if="c.balance !== 0" class="text-base font-bold leading-none"
                :class="c.balance > 0 ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
                Rs {{ formatNum(Math.abs(c.balance)) }}
              </p>
              <p v-else class="text-base font-bold leading-none text-slate-400 dark:text-slate-600">Rs 0</p>
            </div>
          </div>

        </div>
      </div>

    </div>

    <!-- Floating Add Customer Button (Mobile) -->
    <button @click="openAddCustomer"
      class="md:hidden fixed bottom-20 left-1/2 -translate-x-1/2 z-30 flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
             text-sm font-semibold px-8 py-3 rounded-full shadow-lg shadow-indigo-500/40 transition w-64">
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
      </svg>
      Add Customer
    </button>

    <!-- Add Customer Modal -->
    <Modal v-if="showCustomerModal" @close="closeCustomerModal">
      <template #title>Add Customer</template>
      <form @submit.prevent="submitCustomerForm" class="space-y-4">

        <!-- Name (always visible) -->
        <InputField v-model="customerForm.name" label="Full Name" placeholder="Muhammad Aslam" required />

        <!-- More Details Toggle -->
        <button type="button" @click="showCustomerMoreDetails = !showCustomerMoreDetails"
          class="flex items-center gap-2 text-sm font-medium text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition">
          <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': showCustomerMoreDetails }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
          {{ showCustomerMoreDetails ? 'Hide Details' : 'More Details' }}
        </button>

        <!-- Additional Fields (collapsible) -->
        <div v-show="showCustomerMoreDetails" class="space-y-4 pt-2">
          <div class="grid grid-cols-2 gap-3">
            <InputField v-model="customerForm.shop_name" label="Business Name" placeholder="Aslam Hardware" class="col-span-2" />
            <InputField v-model="customerForm.phone_number" label="Phone" placeholder="03001234567" />
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">City</label>
              <div class="relative">
                <select v-model="customerForm.city_id"
                  class="w-full appearance-none bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-4 pr-9 py-3 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer">
                  <option value="" disabled>Select city</option>
                  <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.name }}</option>
                </select>
                <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                </svg>
              </div>
            </div>
            <InputField v-model="customerForm.address" label="Address" placeholder="Shop 5, Main Bazar" class="col-span-2" />
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeCustomerModal"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="customerLoading"
            class="flex-1 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold transition">
            {{ customerLoading ? 'Saving...' : 'Add Customer' }}
          </button>
        </div>
      </form>
    </Modal>

  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage, router, useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import InputField from '@/components/InputField.vue'
import axios from 'axios'

const page = usePage()

const props = defineProps({
  cities: Array
})

const customerBalances = ref([])
const summary = ref({ total_to_receive: 0, total_to_give: 0 })
const dataLoading = ref(true)
const search = ref('')

// Customer modal state
const showCustomerModal = ref(false)
const customerLoading = ref(false)
const showCustomerMoreDetails = ref(false)
const customerForm = ref({
  name: '',
  shop_name: '',
  phone_number: '',
  city_id: '',
  address: '',
  opening_balance_to_receive: 0,
  opening_balance_to_give: 0
})

// Filter customers based on search
const filtered = computed(() => {
  if (!search.value) return customerBalances.value

  const query = search.value.toLowerCase()
  return customerBalances.value.filter(c =>
    c.name.toLowerCase().includes(query) ||
    c.shop_name.toLowerCase().includes(query)
  )
})

async function fetchData() {
  dataLoading.value = true
  try {
    const response = await axios.get('/khata/data')
    customerBalances.value = response.data.customer_balances
    summary.value = response.data.summary
  } catch (error) {
    console.error('Error fetching khata data:', error)
  } finally {
    dataLoading.value = false
  }
}

onMounted(() => {
  fetchData()
})

function formatNum(n) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6','#f97316','#84cc16']
function avatarColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function navigateToDetails(customerId) {
  router.visit(`/khata/${customerId}/details`)
}

// Customer modal functions
function openAddCustomer() {
  customerForm.value = {
    name: '',
    shop_name: '',
    phone_number: '',
    city_id: '',
    address: '',
    opening_balance_to_receive: 0,
    opening_balance_to_give: 0
  }
  showCustomerMoreDetails.value = false
  showCustomerModal.value = true
}

function closeCustomerModal() {
  showCustomerModal.value = false
  showCustomerMoreDetails.value = false
}

function submitCustomerForm() {
  customerLoading.value = true

  // Auto-fill dummy data if "More Details" section was not opened
  if (!showCustomerMoreDetails.value) {
    if (!customerForm.value.shop_name) {
      customerForm.value.shop_name = 'Khata'
    }
    if (!customerForm.value.phone_number) {
      customerForm.value.phone_number = '00000000000'
    }
    if (!customerForm.value.city_id && props.cities && props.cities.length > 0) {
      customerForm.value.city_id = props.cities[0].id
    }
  }

  const req = useForm({ customer: customerForm.value })
  const opts = {
    onSuccess: () => {
      closeCustomerModal()
      fetchData()
    },
    onError: (errors) => {
      console.error('Error adding customer:', errors)
      alert('Error adding customer. Please try again.')
    },
    onFinish: () => {
      customerLoading.value = false
    },
  }
  req.post('/customers', opts)
}
</script>
